import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CellValue } from './CellValue';
import { NEUTRAL_PILL, PILL_BASE, tonePillClasses } from './evidencePrimitives';


interface StatusTransitionProps {
  from?: string | null;
  to?: string | null;
  tone?: string | null;
  className?: string;
}

/**
 * `shape: 'transition'` — `from → to` as two pills (design §1.4 "Status transition").
 *
 * ⚠️ **`tone` is null on almost every row today** — one producer declares a transition at all — so the
 * neutral pill is the common case, not the fallback nobody sees. It must look deliberate.
 *
 * 🚫 The tone is NEVER inferred from the status text. Mapping "Posted" → success would put a client-side
 * status vocabulary in this file, needing lockstep with every module's status set forever; the server owns
 * that word and either declares its tone or does not.
 */
export function StatusTransition({ from, to, tone, className }: StatusTransitionProps) {
  // Both ends absent is an empty block, not two em-dashes pointing at each other.
  if (!from && !to) return null;

  const toPill = tonePillClasses(tone) ?? NEUTRAL_PILL;

  return (
    // Wraps rather than overflowing: two long statuses do not fit side by side at 364px.
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      <span className={cn(PILL_BASE, 'whitespace-nowrap bg-muted text-muted-foreground ring-border')}>
        <CellValue value={from} />
      </span>
      <ArrowRight aria-hidden className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className={cn(PILL_BASE, 'whitespace-nowrap', toPill)}>
        <CellValue value={to} />
      </span>
    </div>
  );
}
